import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, Snowflake, MessageCircle, Users, ArrowRight, CircleDot, Flame } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { Widget } from '@/components/athena/Widget';
import { cn } from '@/lib/cn';
import * as inspectorApi from '@/api/inspector.api';
import type { ChatEnCubeta, EstadoRelacion, Relacion, Salud as DatosSalud, TipoDeChat } from '@/api/inspector.api';

/**
 * SALUD — tu WhatsApp en números, y cada número se abre.
 *
 * Esteban, 22-sep-2026: *"análisis, métricas... muchas funciones con doble clic
 * en botones que me hagan ver en widgets informaciones... súper filtros... pero
 * obviamente manteniendo la simpleza"*.
 *
 * ═══ LA SIMPLEZA ═══
 *
 *   1. Cuatro números arriba. Cada uno se abre.
 *   2. Tres gráficas. Cada barra se abre.
 *   3. Tus relaciones. Cada fila se abre.
 *
 * Nada más en la pantalla. Todo lo demás vive DENTRO de lo que se abre: así el
 * tablero no se satura, y el detalle está a un toque. El "doble clic" que pidió
 * es este: número → lista → chat. En el teléfono no existe el doble clic, así
 * que es un toque, y otro.
 *
 * ═══ LOS FILTROS VIVEN EN LA URL ═══
 *
 * Período, tipo y frente van en la dirección. Volver atrás desde un chat deja
 * el tablero como estaba, y se puede guardar un enlace a "mis grupos, 90 días".
 *
 * ═══ LOS COLORES ═══
 *
 * Violeta para lo tuyo (el color del panel) y naranja para lo que te escriben.
 * Validados con el script de la guía de visualización en los dos modos: el
 * violeta claro del panel no pasaba en modo oscuro y se cambió por #9b7af0.
 * Los estados (activa, enfriándose, fría) usan verde, ámbar y rojo, SIEMPRE
 * con ícono y palabra: nunca solo color.
 */

const PERIODOS = [7, 30, 90] as const;
const TIPOS: Array<{ id: TipoDeChat; nombre: string }> = [
  { id: 'todos', nombre: 'Todos' },
  { id: 'personas', nombre: 'Personas' },
  { id: 'grupos', nombre: 'Grupos' },
];

// Las series. Texto SIEMPRE en tinta: el color va solo en la marca.
const TU = 'fill-[#6d28d9] dark:fill-[#9b7af0]';
const OTROS = 'fill-[#eb6834] dark:fill-[#d95926]';
const TU_FONDO = 'bg-[#6d28d9] dark:bg-[#9b7af0]';
const OTROS_FONDO = 'bg-[#eb6834] dark:bg-[#d95926]';

type Abierto =
  | { tipo: 'esperan' }
  | { tipo: 'enfrian' }
  | { tipo: 'respuesta' }
  | { tipo: 'todos' }
  | { tipo: 'chats'; titulo: string; bajada: string; chats: ChatEnCubeta[] }
  | { tipo: 'persona'; r: Relacion };

// ─────────────────────────────── formatos ───────────────────────────────

function hace(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
}

function duracion(min: number | null): string {
  if (min === null) return '—';
  if (min < 1) return 'menos de 1 min';
  if (min < 60) return `${Math.round(min)} min`;
  if (min < 60 * 24) return `${(min / 60).toFixed(1).replace('.0', '')} h`;
  return `${Math.round(min / 60 / 24)} días`;
}

function diaLargo(dia: string): string {
  return new Date(`${dia}T12:00:00`).toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'long' });
}

function diaCorto(dia: string): string {
  return new Date(`${dia}T12:00:00`).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
}

function hora12(h: number): string {
  const s = h < 12 ? 'a. m.' : 'p. m.';
  const n = h % 12 === 0 ? 12 : h % 12;
  return `${n} ${s}`;
}

const numero = (n: number) => n.toLocaleString('es-DO');

// ─────────────────────────────── piezas ───────────────────────────────

const ESTADO: Record<EstadoRelacion, { nombre: string; clase: string; Icono: typeof Flame }> = {
  activa: { nombre: 'Activa', clase: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', Icono: Flame },
  enfriandose: { nombre: 'Enfriándose', clase: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', Icono: Snowflake },
  fria: { nombre: 'Fría', clase: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300', Icono: Snowflake },
};

function ChipEstado({ estado }: { estado: EstadoRelacion | null }) {
  if (!estado) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        <CircleDot className="h-3 w-3" /> Poca historia
      </span>
    );
  }
  const e = ESTADO[estado];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', e.clase)}>
      <e.Icono className="h-3 w-3" /> {e.nombre}
    </span>
  );
}

/** La mini gráfica de una persona. Una sola serie: no lleva leyenda. */
function MiniSerie({ serie, alto = 24 }: { serie: number[]; alto?: number }) {
  const max = Math.max(1, ...serie);
  // Un mínimo de 2 de alto a cualquier día con mensajes: con un solo mensaje
  // al lado de un día de treinta, la barra quedaba invisible y el día parecía
  // vacío. Fondo tenue para que se lea dónde empieza y termina el período.
  return (
    <svg viewBox={`0 0 ${serie.length} ${alto}`} className="h-6 w-28 rounded-sm bg-slate-50 dark:bg-slate-800/60" preserveAspectRatio="none" aria-hidden="true">
      {serie.map((v, i) => {
        if (v <= 0) return null;
        const h = Math.max(2, (v / max) * alto);
        return <rect key={i} x={i + 0.1} y={alto - h} width={0.8} height={h} className={TU} />;
      })}
    </svg>
  );
}

function Segmentado<T extends string | number>({
  id,
  opciones,
  valor,
  onCambio,
}: {
  id: string;
  opciones: Array<{ id: T; nombre: string }>;
  valor: T;
  onCambio: (v: T) => void;
}) {
  return (
    <div id={id} role="radiogroup" className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
      {opciones.map((o) => (
        <button
          key={String(o.id)}
          type="button"
          role="radio"
          aria-checked={valor === o.id}
          onClick={() => onCambio(o.id)}
          className={cn(
            'min-h-[36px] rounded-md px-3 text-sm transition',
            valor === o.id
              ? 'bg-violet-600 font-medium text-white'
              : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
          )}
        >
          {o.nombre}
        </button>
      ))}
    </div>
  );
}

function Ficha({
  titulo,
  valor,
  pie,
  Icono,
  aviso,
  onAbrir,
}: {
  titulo: string;
  valor: string;
  pie: string;
  Icono: typeof Clock;
  aviso?: boolean;
  onAbrir: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onAbrir}
      className="group flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-violet-300 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-violet-500/60"
    >
      <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
        <Icono className={cn('h-3.5 w-3.5', aviso && 'text-amber-600 dark:text-amber-400')} /> {titulo}
      </span>
      <span className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-50">{valor}</span>
      <span className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        {pie}
        <ArrowRight className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />
      </span>
    </button>
  );
}

function Bloque({ titulo, bajada, children }: { titulo: string; bajada?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{titulo}</h2>
      {bajada && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{bajada}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

// ─────────────────────────────── gráficas ───────────────────────────────

/** Mensajes por día: tú y los demás, apilados. Cada columna se abre. */
function GraficaPorDia({ datos, onDia }: { datos: DatosSalud['porDia']; onDia: (i: number) => void }) {
  const [sobre, setSobre] = useState<number | null>(null);
  const ALTO = 140;
  const max = Math.max(1, ...datos.map((d) => d.tuyos + d.deOtros));
  const ancho = 100 / datos.length;
  // Tres marcas de escala, redondas, que la gráfica alcanza de verdad.
  const tope = Math.ceil(max / 10) * 10 || 10;
  const marcas = [0, tope / 2, tope];
  const esc = (v: number) => (v / tope) * ALTO;
  const cadaCuanto = datos.length > 31 ? 15 : datos.length > 7 ? 7 : 1;

  return (
    <div className="relative">
      <div className="mb-2 flex gap-4 text-xs text-slate-600 dark:text-slate-300">
        <span className="flex items-center gap-1.5"><span className={cn('h-2.5 w-2.5 rounded-sm', TU_FONDO)} /> Tú</span>
        <span className="flex items-center gap-1.5"><span className={cn('h-2.5 w-2.5 rounded-sm', OTROS_FONDO)} /> Te escribieron</span>
      </div>
      <div className="flex gap-2">
        <div className="relative w-7 shrink-0 text-right text-[10px] tabular-nums text-slate-400" style={{ height: ALTO }}>
          {marcas.map((m) => (
            <span key={m} className="absolute right-0 -translate-y-1/2" style={{ top: ALTO - esc(m) }}>{m}</span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1">
          <svg viewBox={`0 0 100 ${ALTO}`} preserveAspectRatio="none" className="block w-full" style={{ height: ALTO }} role="img" aria-label="Mensajes por día">
            {marcas.map((m) => (
              <line key={m} x1={0} x2={100} y1={ALTO - esc(m)} y2={ALTO - esc(m)} className="stroke-slate-100 dark:stroke-slate-800" strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
            ))}
            {datos.map((d, i) => {
              const hOtros = esc(d.deOtros);
              const hTu = esc(d.tuyos);
              const x = i * ancho + ancho * 0.15;
              const w = ancho * 0.7;
              return (
                <g key={d.dia}>
                  {hOtros > 0 && <rect x={x} y={ALTO - hOtros} width={w} height={hOtros} className={OTROS} opacity={sobre === null || sobre === i ? 1 : 0.45} />}
                  {/* 2 px de separación entre los dos tramos, en el color del fondo. */}
                  {hTu > 0 && <rect x={x} y={ALTO - hOtros - hTu - (hOtros > 0 ? 1.5 : 0)} width={w} height={hTu} className={TU} opacity={sobre === null || sobre === i ? 1 : 0.45} />}
                </g>
              );
            })}
          </svg>
          {/* La zona de toque es la columna entera, no la barra: una barra de un
              mensaje sería imposible de tocar en el teléfono. */}
          <div className="absolute inset-0 flex">
            {datos.map((d, i) => (
              <button
                key={d.dia}
                type="button"
                aria-label={`${diaLargo(d.dia)}: ${d.tuyos} tuyos, ${d.deOtros} te escribieron`}
                className="h-full flex-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500"
                onMouseEnter={() => setSobre(i)}
                onMouseLeave={() => setSobre(null)}
                onFocus={() => setSobre(i)}
                onBlur={() => setSobre(null)}
                onClick={() => onDia(i)}
              />
            ))}
          </div>
          {sobre !== null && datos[sobre] && (
            <div
              className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md dark:border-slate-700 dark:bg-slate-900"
              style={{ left: `${(sobre + 0.5) * ancho}%` }}
            >
              <p className="font-medium text-slate-800 first-letter:uppercase dark:text-slate-100">{diaLargo(datos[sobre]!.dia)}</p>
              <p className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300"><span className={cn('h-2 w-2 rounded-sm', TU_FONDO)} /> Tú: {numero(datos[sobre]!.tuyos)}</p>
              <p className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300"><span className={cn('h-2 w-2 rounded-sm', OTROS_FONDO)} /> Te escribieron: {numero(datos[sobre]!.deOtros)}</p>
              <p className="mt-1 text-slate-400">Toca para ver los chats</p>
            </div>
          )}
          <div className="mt-1 flex text-[10px] text-slate-400">
            {datos.map((d, i) => (
              // Sin cortar: "9 jul" en dos renglones se lee como dos datos.
              <span key={d.dia} className="flex-1 whitespace-nowrap text-center">{i % cadaCuanto === (datos.length - 1) % cadaCuanto ? diaCorto(d.dia) : ''}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** A qué hora escribes. Una sola serie: tus mensajes. */
function GraficaPorHora({ datos, onHora }: { datos: DatosSalud['porHora']; onHora: (h: number) => void }) {
  const [sobre, setSobre] = useState<number | null>(null);
  const ALTO = 90;
  const max = Math.max(1, ...datos.map((d) => d.tuyos));
  const pico = datos.reduce((a, b) => (b.tuyos > a.tuyos ? b : a), datos[0]!);
  const madrugada = datos.filter((d) => d.hora >= 0 && d.hora < 5).reduce((a, d) => a + d.tuyos, 0);

  return (
    <div className="relative">
      <div className="relative">
        <svg viewBox={`0 0 24 ${ALTO}`} preserveAspectRatio="none" className="block w-full" style={{ height: ALTO }} role="img" aria-label="Tus mensajes por hora del día">
          {datos.map((d) => {
            const h = (d.tuyos / max) * ALTO;
            return h > 0 ? (
              <rect key={d.hora} x={d.hora + 0.15} y={ALTO - h} width={0.7} height={h} className={TU} opacity={sobre === null || sobre === d.hora ? 1 : 0.45} />
            ) : null;
          })}
        </svg>
        <div className="absolute inset-0 flex">
          {datos.map((d) => (
            <button
              key={d.hora}
              type="button"
              aria-label={`${hora12(d.hora)}: ${d.tuyos} mensajes tuyos`}
              className="h-full flex-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500"
              onMouseEnter={() => setSobre(d.hora)}
              onMouseLeave={() => setSobre(null)}
              onFocus={() => setSobre(d.hora)}
              onBlur={() => setSobre(null)}
              onClick={() => onHora(d.hora)}
            />
          ))}
        </div>
        {sobre !== null && (
          <div
            className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md dark:border-slate-700 dark:bg-slate-900"
            style={{ left: `${((sobre + 0.5) / 24) * 100}%` }}
          >
            <p className="font-medium text-slate-800 dark:text-slate-100">{hora12(sobre)}</p>
            <p className="text-slate-600 dark:text-slate-300">{numero(datos[sobre]!.tuyos)} mensajes tuyos</p>
          </div>
        )}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-400">
        <span>12 a. m.</span><span>6 a. m.</span><span>12 p. m.</span><span>6 p. m.</span><span>11 p. m.</span>
      </div>
      {pico.tuyos > 0 && (
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
          {/* Sin punto final después de la hora: "a. m." ya lo trae, y quedaba
              "a las 9 a. m..". */}
          Donde más escribes: a las {hora12(pico.hora)}
          {madrugada > 0 && ` De madrugada (de 12 a 5 a. m.) escribiste ${numero(madrugada)} ${madrugada === 1 ? 'mensaje' : 'mensajes'}.`}
        </p>
      )}
    </div>
  );
}

/** El equilibrio entre tus frentes. Color = el que tú le pusiste al frente. */
function GraficaFrentes({ datos, onFrente }: { datos: DatosSalud['porFrente']; onFrente: (i: number) => void }) {
  const total = Math.max(1, datos.reduce((a, d) => a + d.mensajes, 0));
  const max = Math.max(1, ...datos.map((d) => d.mensajes));
  return (
    <div className="space-y-1.5">
      {datos.map((d, i) => (
        <button
          key={d.frenteId ?? 'sin'}
          type="button"
          onClick={() => onFrente(i)}
          className="grid w-full grid-cols-[7rem_1fr_3rem] items-center gap-2 rounded-md px-1 py-1 text-left text-sm hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 dark:hover:bg-slate-800"
        >
          <span className="truncate text-slate-700 dark:text-slate-200">{d.nombre}</span>
          <span className="h-3 rounded-sm bg-slate-100 dark:bg-slate-800">
            <span
              className="block h-3 rounded-sm"
              style={{ width: `${(d.mensajes / max) * 100}%`, backgroundColor: d.color ?? 'rgb(148 163 184)' }}
            />
          </span>
          <span className="text-right text-xs tabular-nums text-slate-500">{Math.round((d.mensajes / total) * 100)}%</span>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────── las listas ───────────────────────────────

function FilaChat({ c, derecha, onAbrir }: { c: { conversationId: string; quien: string }; derecha: React.ReactNode; onAbrir: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onAbrir(c.conversationId)}
      className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg px-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
    >
      <span className="truncate text-slate-800 dark:text-slate-100">{c.quien}</span>
      <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">{derecha}<ArrowRight className="h-3.5 w-3.5" /></span>
    </button>
  );
}

function Vacio({ texto }: { texto: string }) {
  return <p className="py-6 text-center text-sm text-slate-500">{texto}</p>;
}

// ─────────────────────────────── la pantalla ───────────────────────────────

export default function Salud() {
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const dias = PERIODOS.includes(Number(params.get('dias')) as 7) ? Number(params.get('dias')) : 30;
  const tipo = (TIPOS.find((t) => t.id === params.get('tipo'))?.id ?? 'todos') as TipoDeChat;
  const frenteId = params.get('frente') ?? '';
  const [abierto, setAbierto] = useState<Abierto | null>(null);

  const cambiar = (clave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(clave, valor);
    else p.delete(clave);
    setParams(p, { replace: true });
  };

  const q = useQuery({
    queryKey: ['salud', dias, tipo, frenteId],
    queryFn: () => inspectorApi.getSalud({ dias, tipo, ...(frenteId ? { frenteId } : {}) }),
    // Mientras cambia un filtro se queda lo anterior en pantalla: un tablero
    // que parpadea en blanco con cada toque cansa.
    placeholderData: (previo) => previo,
  });

  const s = q.data;
  const abrirChat = (id: string) => navegar(`/conversations/${id}`);

  const personas = useMemo(() => (s ? s.relaciones.filter((r) => !r.esGrupo) : []), [s]);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="space-y-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Salud</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">Tu WhatsApp en números. Toca cualquier número o barra para abrirlo.</p>
        </div>
        {/* Los filtros, en UNA fila, arriba de todo lo que filtran. */}
        <div className="flex flex-wrap items-center gap-2">
          <Segmentado id="filtro-periodo" opciones={PERIODOS.map((d) => ({ id: d, nombre: `${d} días` }))} valor={dias} onCambio={(v) => cambiar('dias', String(v))} />
          <Segmentado id="filtro-tipo" opciones={TIPOS} valor={tipo} onCambio={(v) => cambiar('tipo', v === 'todos' ? '' : v)} />
          <label htmlFor="filtro-frente" className="sr-only">Frente</label>
          <select
            id="filtro-frente"
            value={frenteId}
            onChange={(e) => cambiar('frente', e.target.value)}
            className="min-h-[38px] rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <option value="">Todos los frentes</option>
            {s?.frentes.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            <option value="sin">Sin clasificar</option>
          </select>
          {q.isFetching && <Spinner />}
        </div>
      </header>

      {q.isPending ? (
        <Spinner />
      ) : !s ? (
        // Un 404 no es una falla: es un negocio sin Athena encendida (el
        // servidor no le da estos datos a quien no tiene el módulo). Decir "no
        // se pudo" haría pensar que algo está roto.
        <p className="text-sm text-slate-500">
          {(q.error as { status?: number } | null)?.status === 404
            ? 'Este negocio no tiene Athena encendida, así que no hay tablero de salud.'
            : 'No se pudo leer el tablero. Recarga la página en un momento.'}
        </p>
      ) : (
        <>
          {/* ── 1. LOS CUATRO NÚMEROS ── */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Ficha titulo="Te esperan" valor={numero(s.totales.teEsperan)} pie="personas sin respuesta" Icono={Clock} aviso={s.totales.teEsperan > 0} onAbrir={() => setAbierto({ tipo: 'esperan' })} />
            <Ficha titulo="Se enfrían" valor={numero(s.totales.seEnfrian)} pie="más callados que su costumbre" Icono={Snowflake} aviso={s.totales.seEnfrian > 0} onAbrir={() => setAbierto({ tipo: 'enfrian' })} />
            <Ficha titulo="Tardas en contestar" valor={duracion(s.totales.minutosMedianosRespuesta)} pie="la mitad de las veces, menos" Icono={MessageCircle} onAbrir={() => setAbierto({ tipo: 'respuesta' })} />
            <Ficha titulo="Hablaste con" valor={numero(s.totales.chatsActivos)} pie={`chats en ${s.dias} días`} Icono={Users} onAbrir={() => setAbierto({ tipo: 'todos' })} />
          </div>

          {/* ── 2. LAS GRÁFICAS ── */}
          <Bloque titulo="Tus días" bajada={`${numero(s.totales.tuyos)} mensajes tuyos y ${numero(s.totales.deOtros)} que te escribieron.`}>
            <GraficaPorDia
              datos={s.porDia}
              onDia={(i) => {
                const d = s.porDia[i]!;
                setAbierto({ tipo: 'chats', titulo: diaLargo(d.dia), bajada: `${d.tuyos + d.deOtros} mensajes ese día`, chats: d.chats });
              }}
            />
          </Bloque>

          <div className="grid gap-4 lg:grid-cols-2">
            <Bloque titulo="A qué hora escribes" bajada="Solo tus mensajes. Es la pregunta de salud: ¿contestas de madrugada?">
              <GraficaPorHora
                datos={s.porHora}
                onHora={(h) => setAbierto({ tipo: 'chats', titulo: `A las ${hora12(h)}`, bajada: 'Con quién escribías a esa hora', chats: s.porHora[h]!.chats })}
              />
            </Bloque>
            <Bloque titulo="Tus frentes" bajada="Ninguno vale más que otro. Esto muestra si tu atención lo cumple.">
              <GraficaFrentes
                datos={s.porFrente}
                onFrente={(i) => {
                  const f = s.porFrente[i]!;
                  setAbierto({ tipo: 'chats', titulo: f.nombre, bajada: `${numero(f.mensajes)} mensajes`, chats: f.chats });
                }}
              />
              {s.porFrente.some((f) => f.frenteId === null) && (
                <button type="button" onClick={() => navegar('/chats')} className="mt-3 text-xs text-violet-600 hover:underline dark:text-violet-300">
                  Clasifica tus chats en Frentes →
                </button>
              )}
            </Bloque>
          </div>

          {/* ── 3. TUS RELACIONES ── */}
          <Bloque titulo="Tus relaciones" bajada="Las más activas del período. Cada una se compara con su propia costumbre, no con un número fijo.">
            {s.relaciones.length === 0 ? (
              <Vacio texto="No hubo conversación en este período." />
            ) : (
              <div className="-mx-2 overflow-x-auto">
                <table className="w-full min-w-[34rem] text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500">
                      <th className="px-2 py-1.5 font-medium">Con quién</th>
                      <th className="px-2 py-1.5 font-medium">Estado</th>
                      <th className="px-2 py-1.5 font-medium">Última vez</th>
                      <th className="px-2 py-1.5 font-medium">En el período</th>
                      <th className="px-2 py-1.5 text-right font-medium">Mensajes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.relaciones.map((r) => (
                      <tr
                        key={r.conversationId}
                        tabIndex={0}
                        onClick={() => setAbierto({ tipo: 'persona', r })}
                        onKeyDown={(e) => e.key === 'Enter' && setAbierto({ tipo: 'persona', r })}
                        className="cursor-pointer border-t border-slate-100 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 dark:border-slate-800 dark:hover:bg-slate-800/60"
                      >
                        <td className="max-w-[12rem] truncate px-2 py-2 text-slate-800 dark:text-slate-100">
                          {r.quien}
                          {r.esGrupo && <span className="ml-1.5 text-xs text-slate-400">grupo</span>}
                          {r.teEspera && !r.esGrupo && <span className="ml-1.5 text-xs text-amber-700 dark:text-amber-300">te espera</span>}
                        </td>
                        <td className="px-2 py-2"><ChipEstado estado={r.estado} /></td>
                        <td className="px-2 py-2 text-xs text-slate-500">{hace(r.ultimoContacto)}</td>
                        <td className="px-2 py-2"><MiniSerie serie={r.serie} /></td>
                        <td className="px-2 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{numero(r.mensajes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Bloque>
        </>
      )}

      {/* ── LO QUE SE ABRE ── */}
      {s && abierto && (
        <Widget
          abierto
          onCerrar={() => setAbierto(null)}
          titulo={
            abierto.tipo === 'esperan' ? 'Te esperan'
              : abierto.tipo === 'enfrian' ? 'Se enfrían'
                : abierto.tipo === 'respuesta' ? 'Lo que tardas, por persona'
                  : abierto.tipo === 'todos' ? 'Con quién hablaste'
                    : abierto.tipo === 'chats' ? abierto.titulo.charAt(0).toUpperCase() + abierto.titulo.slice(1)
                      : abierto.r.quien
          }
          bajada={
            abierto.tipo === 'esperan' ? 'Su último mensaje es de ellos. Los que más llevan esperando, primero.'
              : abierto.tipo === 'enfrian' ? 'Callados más tiempo del que suelen estarlo contigo.'
                : abierto.tipo === 'respuesta' ? 'Los que más esperan tu respuesta, primero.'
                  : abierto.tipo === 'chats' ? abierto.bajada
                    : abierto.tipo === 'persona' ? (abierto.r.esGrupo ? 'Grupo' : undefined)
                      : undefined
          }
        >
          {abierto.tipo === 'esperan' && (
            personas.filter((r) => r.teEspera).length === 0 ? <Vacio texto="Nadie te está esperando. Bien." /> :
              [...personas].filter((r) => r.teEspera).sort((a, b) => (a.esperaDesde ?? '').localeCompare(b.esperaDesde ?? ''))
                .map((r) => <FilaChat key={r.conversationId} c={r} derecha={hace(r.esperaDesde!)} onAbrir={abrirChat} />)
          )}
          {abierto.tipo === 'enfrian' && (
            personas.filter((r) => r.estado === 'enfriandose' || r.estado === 'fria').length === 0 ? <Vacio texto="Ninguna relación se está enfriando." /> :
              personas.filter((r) => r.estado === 'enfriandose' || r.estado === 'fria').sort((a, b) => b.diasSinHablar - a.diasSinHablar)
                .map((r) => (
                  <FilaChat key={r.conversationId} c={r} onAbrir={abrirChat}
                    derecha={<><ChipEstado estado={r.estado} /> {r.diasSinHablar} días · suelen cada {r.ritmoDias}</>} />
                ))
          )}
          {abierto.tipo === 'respuesta' && (
            personas.filter((r) => r.minutosRespuesta !== null).length === 0 ? <Vacio texto="Todavía no hay respuestas que medir." /> :
              [...personas].filter((r) => r.minutosRespuesta !== null).sort((a, b) => b.minutosRespuesta! - a.minutosRespuesta!)
                .map((r) => <FilaChat key={r.conversationId} c={r} derecha={duracion(r.minutosRespuesta)} onAbrir={abrirChat} />)
          )}
          {abierto.tipo === 'todos' && s.relaciones.map((r) => (
            <FilaChat key={r.conversationId} c={r} derecha={`${numero(r.mensajes)} mensajes`} onAbrir={abrirChat} />
          ))}
          {abierto.tipo === 'chats' && (
            abierto.chats.length === 0 ? <Vacio texto="No hubo mensajes." /> :
              abierto.chats.map((c) => <FilaChat key={c.conversationId} c={c} derecha={`${numero(c.mensajes)} mensajes`} onAbrir={abrirChat} />)
          )}
          {abierto.tipo === 'persona' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <ChipEstado estado={abierto.r.estado} />
                {abierto.r.teEspera && !abierto.r.esGrupo && <span className="text-xs text-amber-700 dark:text-amber-300">te espera desde {hace(abierto.r.esperaDesde!)}</span>}
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-500">Mensajes por día, últimos {s.dias}</p>
                <svg viewBox={`0 0 ${abierto.r.serie.length} 60`} preserveAspectRatio="none" className="block h-16 w-full" aria-hidden="true">
                  {abierto.r.serie.map((v, i) => {
                    const max = Math.max(1, ...abierto.r.serie);
                    const h = (v / max) * 60;
                    return h > 0 ? <rect key={i} x={i + 0.15} y={60 - h} width={0.7} height={h} className={TU} /> : null;
                  })}
                </svg>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-xs text-slate-500">Mensajes</dt><dd className="tabular-nums text-slate-800 dark:text-slate-100">{numero(abierto.r.mensajes)} ({numero(abierto.r.tuyos)} tuyos)</dd></div>
                <div><dt className="text-xs text-slate-500">Última vez</dt><dd className="text-slate-800 dark:text-slate-100">{hace(abierto.r.ultimoContacto)}</dd></div>
                <div><dt className="text-xs text-slate-500">Suelen hablar</dt><dd className="text-slate-800 dark:text-slate-100">{abierto.r.ritmoDias === null ? 'poca historia' : `cada ${abierto.r.ritmoDias} días`}</dd></div>
                <div><dt className="text-xs text-slate-500">Tardas en contestarle</dt><dd className="text-slate-800 dark:text-slate-100">{duracion(abierto.r.minutosRespuesta)}</dd></div>
              </dl>
              <button
                type="button"
                onClick={() => abrirChat(abierto.r.conversationId)}
                className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-violet-600 text-sm font-medium text-white hover:bg-violet-700"
              >
                Abrir el chat <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </Widget>
      )}
    </div>
  );
}
